import { lazyRetry } from './utils/lazyRetry'
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom'
import { AnimatePresence } from 'framer-motion'

import { AuthProvider } from './contexts/AuthContext'
import { CurrentActionProvider } from './contexts/CurrentActionContext'
import { TutorialProvider } from './contexts/TutorialContext'
import TutorialOverlay from './components/Tutorial/TutorialOverlay'
import { RootProviders, CelebrationProvider } from './providers'
import ErrorBoundary from './components/ErrorBoundary'

// Layouts & Auth
import PublicLayout from './layouts/PublicLayout'
import AuthenticatedLayout from './layouts/AuthenticatedLayout'
import RequireAuth from './components/Auth/RequireAuth'
import AuthCallback from './components/AuthCallback'
import PublicLanding from './components/Auth/PublicLanding'
import UsernameCheck from './components/UsernameCheck'
import PageTransition from './components/ui/PageTransition'
import VerifyEmail from './components/Auth/VerifyEmail'
import ResetPassword from './components/Auth/ResetPassword'

// Lazy load view components
const ShelterView = lazyRetry(() => import('./components/Shelter/ShelterView'))
const ScavengingView = lazyRetry(() => import('./components/Scavenging/ScavengingView'))
const CraftingView = lazyRetry(() => import('./components/Crafting/CraftingView'))
const UpgradesView = lazyRetry(() => import('./components/Crafting/UpgradesView'))
const BlueprintsView = lazyRetry(() => import('./components/Blueprints/BlueprintsView'))
const CodexView = lazyRetry(() => import('./components/Codex/CodexView'))
const EventsLogView = lazyRetry(() => import('./components/EventsLog/EventsLogView'))
const SkillsView = lazyRetry(() => import('./components/Skills/SkillsView'))
const GatheringView = lazyRetry(() => import('./components/Gathering/GatheringView'))
const VendorsView = lazyRetry(() => import('./components/Vendors/VendorsView'))
const RoomsView = lazyRetry(() => import('./components/Rooms/RoomsView'))
const AchievementsView = lazyRetry(() => import('./components/Achievements').then(m => ({ default: m.AchievementsView })))
const MarketView = lazyRetry(() => import('./components/Market').then(m => ({ default: m.MarketView })))
const CharacterManagement = lazyRetry(() => import('./components/CharacterManagement/CharacterManagement'))
const ProfileView = lazyRetry(() => import('./components/Profile/ProfileView'))
const DailyRewardView = lazyRetry(() => import('./components/DailyRewards/DailyRewardView'))
const ContractsView = lazyRetry(() => import('./components/Contracts').then(m => ({ default: m.default })))
const TechTreeView = lazyRetry(() => import('./components/TechTree/TechTreeView'))
const ActiveScavengingRunPage = lazyRetry(() => import('./pages/ActiveScavengingRunPage'))
const ActiveRun2DPage = lazyRetry(() => import('./pages/ActiveRun2DPage'))
const GatheringWorkbenchPage = lazyRetry(() => import('./pages/GatheringWorkbenchPage'))
const AdminView = lazyRetry(() => import('./components/Admin/AdminView'))
const CheatView = lazyRetry(() => import('./components/Cheat/CheatView'))
const NotFound = lazyRetry(() => import('./components/NotFound'))

function AppRoutes() {
  return (
    <AnimatePresence mode="wait">
      <Routes>
        {/* Public Routes */}
        <Route element={<PublicLayout />}>
          <Route path="/" element={<PublicLanding />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/reset-password" element={<ResetPassword />} />
        </Route>

        {/* Protected Routes */}
        <Route element={<RequireAuth />}>
          <Route element={
            <UsernameCheck>
              <AuthenticatedLayout />
            </UsernameCheck>
          }>
            <Route path="/shelter" element={<PageTransition><ShelterView /></PageTransition>} />
            <Route path="/scavenging" element={<PageTransition><ScavengingView /></PageTransition>} />
            <Route path="/scavenging/run" element={<PageTransition><ActiveScavengingRunPage /></PageTransition>} />
            <Route path="/scavenging/manual" element={<PageTransition><ActiveRun2DPage /></PageTransition>} />
            <Route path="/crafting" element={<PageTransition><CraftingView /></PageTransition>} />
            <Route path="/upgrades" element={<PageTransition><UpgradesView /></PageTransition>} />
            <Route path="/blueprints" element={<PageTransition><BlueprintsView /></PageTransition>} />
            <Route path="/codex" element={<PageTransition><CodexView /></PageTransition>} />
            <Route path="/events" element={<PageTransition><EventsLogView /></PageTransition>} />
            <Route path="/skills" element={<PageTransition><SkillsView /></PageTransition>} />
            <Route path="/gathering" element={<PageTransition><GatheringView /></PageTransition>} />
            <Route path="/gathering/:gatheringId" element={<PageTransition><GatheringWorkbenchPage /></PageTransition>} />
            <Route path="/vendors" element={<PageTransition><VendorsView /></PageTransition>} />
            <Route path="/market" element={<PageTransition><MarketView /></PageTransition>} />
            <Route path="/rooms" element={<PageTransition><RoomsView /></PageTransition>} />
            <Route path="/tech-tree" element={<PageTransition><TechTreeView /></PageTransition>} />
            <Route path="/achievements" element={<PageTransition><AchievementsView /></PageTransition>} />
            <Route path="/profile/:handle" element={<PageTransition><ProfileView /></PageTransition>} />
            <Route path="/daily-rewards" element={<PageTransition><DailyRewardView /></PageTransition>} />
            <Route path="/contracts" element={<PageTransition><ContractsView /></PageTransition>} />
            <Route path="/characters" element={<PageTransition><CharacterManagement /></PageTransition>} />
            <Route path="/admin" element={<PageTransition><AdminView /></PageTransition>} />
            <Route path="/cheat" element={<PageTransition><CheatView /></PageTransition>} />

            {/* Unknown URLs render INSIDE the application shell for signed-in users (M13),
                so navigation survives a mistyped route. Signed-out visitors never reach this
                element: the wrapping RequireAuth redirects them to / with `state.from` set,
                which is also what preserves deep links across login (M14). */}
            <Route path="*" element={<PageTransition><NotFound /></PageTransition>} />
          </Route>
        </Route>
      </Routes>
    </AnimatePresence>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <RootProviders>
        <AuthProvider>
          <CurrentActionProvider>
            <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <CelebrationProvider>
                <TutorialProvider>
                  <AppRoutes />
                  <TutorialOverlay />
                </TutorialProvider>
              </CelebrationProvider>
            </Router>
          </CurrentActionProvider>
        </AuthProvider>
      </RootProviders>
    </ErrorBoundary>
  )
}

export default App
